/**
 * Centralized configuration for user upload and download limits.
 * All limits are enforced on a rolling 30-day window.
 * 
 * NOTE: These values should match the backend configuration in UserLimitsOptions.cs
 * The backend is authoritative; these are used for UI display and client-side hints only.
 */

export enum UserTier {
  Anonymous = 0,
  FreeAccount = 1,
  LicensedAccount = 2,
}

export interface TierLimits {
  uploadMB: number;
}

export interface LimitsConfig {
  rollingWindowDays: number;
  safetyMargin: number;
  encryptionOverhead: number;
  anonymous: TierLimits;
  freeAccount: TierLimits;
  licensedAccount: TierLimits;
}

/**
 * User limits configuration
 * All size values in megabytes (MB)
 */
export const USER_LIMITS: LimitsConfig = {
  // Rolling window period in days
  rollingWindowDays: 30,

  // Safety margin (20% = 0.20)
  safetyMargin: 0.20,

  // Encryption overhead multiplier (2x)
  encryptionOverhead: 2.0,

  // Anonymous user limits
  anonymous: {
    uploadMB: 500,              // 500 MB per 30 days
  },

  // Free account limits
  freeAccount: {
    uploadMB: 3072,             // 3 GB per 30 days
  },

  // Licensed account limits
  licensedAccount: {
    uploadMB: 51200,            // 50 GB per 30 days
  },
};

/**
 * Get limits for a specific user tier
 */
export function getLimitsForTier(tier: UserTier): TierLimits {
  switch (tier) {
    case UserTier.Anonymous:
      return USER_LIMITS.anonymous;
    case UserTier.FreeAccount:
      return USER_LIMITS.freeAccount;
    case UserTier.LicensedAccount:
      return USER_LIMITS.licensedAccount;
    default:
      return USER_LIMITS.anonymous;
  }
}

/**
 * Convert MB to bytes
 */
export function mbToBytes(mb: number): number {
  return mb * 1024 * 1024;
}

/**
 * Convert bytes to MB
 */
export function bytesToMB(bytes: number): number {
  return bytes / (1024 * 1024);
}

/**
 * Convert GB to MB
 */
export function gbToMB(gb: number): number {
  return gb * 1024;
}

/**
 * Convert MB to GB
 */
export function mbToGB(mb: number): number {
  return mb / 1024;
}

/**
 * Estimate encrypted file size from plain file size (in bytes)
 */
export function estimateEncryptedSize(plainSizeBytes: number): number {
  return plainSizeBytes * USER_LIMITS.encryptionOverhead;
}

/**
 * Estimate encrypted file size with safety margin (in bytes)
 */
export function estimateEncryptedSizeWithMargin(plainSizeBytes: number): number {
  const encrypted = estimateEncryptedSize(plainSizeBytes);
  return encrypted * (1 + USER_LIMITS.safetyMargin);
}

/**
 * Get upload limit with margin applied (in bytes)
 */
export function getUploadLimitBytesWithMargin(tier: UserTier): number {
  const limits = getLimitsForTier(tier);
  const limitBytes = mbToBytes(limits.uploadMB);
  return limitBytes * (1 + USER_LIMITS.safetyMargin);
}

/**
 * Format bytes to human-readable size with appropriate unit
 */
export function formatSize(bytes: number, decimals: number = 2): string {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(decimals)) + ' ' + sizes[i];
}

/**
 * Calculate percentage used
 */
export function calculateUsagePercentage(used: number, limit: number): number {
  if (limit === 0) return 0;
  return Math.min(100, Math.round((used / limit) * 100));
}

/**
 * Check if user is near limit (>80%)
 */
export function isNearLimit(used: number, limit: number): boolean {
  return calculateUsagePercentage(used, limit) >= 80;
}

/**
 * Check if user exceeded limit
 */
export function isOverLimit(used: number, limit: number): boolean {
  return used >= limit;
}
